import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import chartVisits from '@/assets/chart-visits.webp';
import chartSales from '@/assets/chart-sales.webp';
import mapSumy from '@/assets/map-sumy.webp';

export default function StorySection() {
  const { t } = useLanguage();

  const stats = [
    { value: t.story.stat1, label: t.story.stat1Label },
    { value: t.story.stat2, label: t.story.stat2Label },
    { value: t.story.stat3, label: t.story.stat3Label },
  ];

  return (
    <section id="story" className="section-padding section-alt">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
            {t.story.badge}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t.story.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.story.subtitle}
          </p>
        </motion.div>

        {/* Stats */}
        <div className="text-center mb-6">
          <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider">
            {t.story.outcomesLabel}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-6 mb-16">
          {stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              className="text-center card-elevated p-6 md:p-8"
            >
              <div className="stat-number">{s.value}</div>
              <div className="text-sm md:text-base text-muted-foreground mt-2">{s.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Narrative */}
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-5"
          >
            <p className="text-foreground/90 leading-relaxed">{t.story.p1}</p>
            <p className="text-foreground/90 leading-relaxed">{t.story.p2}</p>
            <p className="text-foreground/90 leading-relaxed font-medium">{t.story.p3}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <img
              src={mapSumy}
              alt={t.story.chartMap}
              width={1224}
              height={812}
              loading="lazy"
              decoding="async"
              className="rounded-xl shadow-lg w-full"
            />
          </motion.div>
        </div>

        {/* Charts */}
        <div className="grid md:grid-cols-2 gap-8">
          {[
            { src: chartVisits, alt: t.story.chartVisits, w: 1140, h: 657 },
            { src: chartSales, alt: t.story.chartSales, w: 1140, h: 669 },
          ].map((chart, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.2 }}
              className="card-elevated p-4 md:p-6"
            >
              <img
                src={chart.src}
                alt={chart.alt}
                width={chart.w}
                height={chart.h}
                loading="lazy"
                decoding="async"
                className="w-full rounded-lg"
              />
              <figcaption className="mt-3 text-center text-sm md:text-base font-medium text-foreground/80">
                {chart.alt}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
